import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CrudList, CrudStyles } from '../../shared/Crud';
import driverService from '../../../services/driverService';
import { DELIVERY_DRIVER_LIST_COLUMNS } from './deliveryDriverListColumns';

const { Button } = CrudStyles;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${({ $zIndex }) => $zIndex ?? 9999};
`;

const ModalBox = styled.div`
    box-sizing: border-box;
    background: #fff;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    max-width: min(900px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    overflow: hidden;
    min-width: 0;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-shrink: 0;

    h2 {
        margin: 0;
        font-size: 18px;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #666;
    font-size: 24px;
    line-height: 1;
    &:hover {
        color: #222;
    }
`;

export default function DeliveryDriverSelectorModal({
    open,
    currentDriverId,
    onClose,
    onSelectDriver,
    onClearDriver,
    isSubmitting = false,
    removalConfirmMessage = 'Deseja remover o motorista desta entrega?',
    removeButtonTitle = 'Remover motorista da entrega',
    zIndex = 9999,
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });

    useEffect(() => {
        if (!open) return;
        setData([]);
        setPagination({ page: 0, totalPages: 0, first: true, last: true });
        setLoadError(null);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const result = await driverService.listDrivers({
                    page: pagination.page,
                    size: 10,
                    sort: 'name,asc',
                });

                if (cancelled) return;

                const content = result.content || [];
                const totalPages =
                    result.page?.totalPages != null && Number.isFinite(Number(result.page.totalPages))
                        ? Number(result.page.totalPages)
                        : content.length === 0
                          ? 0
                          : 1;

                const pageNumber =
                    result.page?.number != null && Number.isFinite(Number(result.page.number))
                        ? Number(result.page.number)
                        : pagination.page;

                setData(content);
                setPagination({
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                });
            } catch (e) {
                if (!cancelled) {
                    setLoadError('Não foi possível carregar os motoristas.');
                    setData([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, pagination.page]);

    const handlePageChange = (newPage) => {
        setPagination((p) => ({ ...p, page: newPage }));
    };

    const renderActions = (item) => {
        const isCurrent = currentDriverId != null && String(currentDriverId) === String(item?.id);
        return (
            <Button
                type="button"
                variant="primary"
                disabled={isSubmitting || isCurrent}
                onClick={() => onSelectDriver?.(item)}
                title={isCurrent ? 'Este é o motorista atual' : 'Selecionar motorista'}
                style={{ justifyContent: 'center' }}
            >
                {isSubmitting ? 'Salvando...' : isCurrent ? 'Atual' : 'Selecionar'}
            </Button>
        );
    };

    if (!open) return null;

    return (
        <Overlay
            $zIndex={zIndex}
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.target === e.currentTarget && !isSubmitting && onClose?.()}
        >
            <ModalBox onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <h2>Selecionar motorista</h2>
                    <CloseBtn type="button" onClick={() => !isSubmitting && onClose?.()} title="Fechar">
                        ×
                    </CloseBtn>
                </ModalHeader>

                {loadError && (
                    <div style={{ marginBottom: 12 }} role="alert">
                        <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                    </div>
                )}

                <CrudList
                    hideHeader
                    title="Motoristas"
                    tableMinWidth="520px"
                    columns={DELIVERY_DRIVER_LIST_COLUMNS}
                    data={data}
                    loading={loading}
                    actions={renderActions}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                />

                {currentDriverId != null && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
                        <Button
                            type="button"
                            variant="danger"
                            disabled={isSubmitting}
                            onClick={() => {
                                const ok = window.confirm(removalConfirmMessage);
                                if (!ok) return;
                                onClearDriver?.();
                            }}
                            title={removeButtonTitle}
                        >
                            Remover motorista
                        </Button>
                    </div>
                )}
            </ModalBox>
        </Overlay>
    );
}
